import * as React from "react";
import { Table } from "antd";
import { WrappedComponentProps, injectIntl } from "react-intl";
import { Position, Currency } from "swan-public-api-client";
import { PaginationConfig } from "antd/lib/pagination";
import { connect } from "react-redux";
import { AppState } from "../Store/RootReducer";
import { ColumnProps, TableLocale } from "antd/lib/table";
import { currencyRoundedValueRenderer, priceWithCurrencyViaRecordRenderer, priceRenderer } from "../Grid/Renderers";
import { FxConvertedPosition } from "./FxConvertedPosition";

interface PropsFromState {
  currencies: Map<number, Currency>;
  baseCurrency: Currency;
}

interface PositionsGridProps {
  columns: Column[];
  bordered?: boolean;
  locale?: TableLocale;
  pagination?: PaginationConfig | false;
  title?: boolean | ((currentPageData: object[]) => React.ReactNode);
  onSelectionChange?: (
    selectedRowKeys: string[] | number[],
    selectedRows: FxConvertedPosition[]
  ) => void;
}

export type Column = "marketName" | "quantity" | "bookingPrice" | "currentPrice" | "profitAndLoss" | "margin";

export const DefaultColumns: Column[] = ["marketName", "quantity", "bookingPrice", "currentPrice", "profitAndLoss", "margin"];
export const DefaultSpotFxColumns: Column[] = ["marketName", "quantity", "bookingPrice", "currentPrice", "profitAndLoss"];

interface Props
  extends WrappedComponentProps,
    PropsFromState,
    PositionsGridProps {
  positions: FxConvertedPosition[];
}

function PositionsGridCmp(props: Props) {
  const {
    Symbol: baseCurrencySymbol
  } = props.baseCurrency;

  const columns: Array<ColumnProps<FxConvertedPosition>> = props.columns.map(column => {
    switch (column) {
      case "marketName":
        return {
          dataIndex: "MarketName",
          title: props.intl.formatMessage({ id: "positionsGrid.marketName" }),
          sorter: (a, b) => (a.MarketName < b.MarketName ? -1 : 1),
          sortDirections: ["ascend", "descend"],
          defaultSortOrder: "ascend"
        };
      case "quantity":
        return {
          dataIndex: "Quantity",
          title: props.intl.formatMessage({ id: "positionsGrid.quantity" }),
          render: currencyRoundedValueRenderer(
            x => x.Quantity,
            x => x.CurrencyId,
            props.currencies
          ),
          align: "right",
          sorter: (a, b) => (Math.abs(a.Quantity) < Math.abs(b.Quantity) ? -1 : 1),
          sortDirections: ["ascend", "descend"]
        };
      case "currentPrice":
        return {
          dataIndex: "CurrentPrice",
          title: props.intl.formatMessage({
            id: "positionsGrid.currentPrice"
          }),
          render: priceWithCurrencyViaRecordRenderer(
            x => x.CurrentPrice,
            _ => undefined,
            props.currencies
          ),
          align: "right",
          sorter: (a, b) => (a.CurrentPrice < b.CurrentPrice ? -1 : 1),
          sortDirections: ["ascend", "descend"]
        };
      case "bookingPrice":
        return {
          dataIndex: "BookingPrice",
          title: props.intl.formatMessage({
            id: "positionsGrid.netOpenPrice"
          }),
          render: priceWithCurrencyViaRecordRenderer(
            x => x.BookingPrice,
            _ => undefined,
            props.currencies
          ),
          align: "right",
          sorter: (a, b) => (a.BookingPrice < b.BookingPrice ? -1 : 1),
          sortDirections: ["ascend", "descend"]
        };
      case "profitAndLoss":
        return {
          dataIndex: "BaseCurrencyOpenPnL",
          render: priceRenderer(
            x => x.BaseCurrencyOpenPnL,
            props.baseCurrency
          ),
          title: props.intl.formatMessage(
            {
              id: "positionsGrid.baseCurrencyOpenPnL"
            },
            { symbol: baseCurrencySymbol }
          ),
          align: "right",
          sorter: (a, b) =>
            (a.BaseCurrencyOpenPnL || -1) < (b.BaseCurrencyOpenPnL || -1) ? -1 : 1,
          sortDirections: ["ascend", "descend"]
        };
      case "margin":
        return {
          dataIndex: "BaseCurrencyMarginRequirement",
          render: priceRenderer(
            x => x.BaseCurrencyMarginRequirement,
            props.baseCurrency
          ),
          title: props.intl.formatMessage(
            {
              id: "positionsGrid.baseCurrencyMargin"
            },
            { symbol: baseCurrencySymbol }
          ),
          align: "right",
          sorter: (a, b) =>
            (a.BaseCurrencyOpenPnL || -1) < (b.BaseCurrencyOpenPnL || -1) ? -1 : 1,
          sortDirections: ["ascend", "descend"]
        };
      default:
        throw Error(`Unknown column "${column}"`);
    }
  });

  return (
    <Table
      dataSource={props.positions}
      rowKey={(record: Position) => record.PositionKey.Key}
      columns={columns}
      size="middle"
      bordered={props.bordered}
      title={props.title === undefined || props.title === false
        ? undefined
        : props.title === true
          ? () => props.intl.formatMessage({ id: "positionsGrid.title" })
          : props.title}
      locale={props.locale}
      pagination={props.pagination}
      rowSelection={
        props.onSelectionChange
          ? {
            type: "radio",
            onChange: props.onSelectionChange
          }
          : undefined
      }
    />
  );
}

const PositionsGridWithIntl = injectIntl(PositionsGridCmp);

const mapStateToProps = (state: AppState): PropsFromState => ({
  currencies: new Map<number, Currency>(
    state.activeTradingAccount.account!.Currencies.map(x => [
      parseInt(x.Key, 10),
      x
    ])
  ),
  baseCurrency: state.clientAccount.account!.Currency
});

export const PositionsGrid = connect(mapStateToProps)(PositionsGridWithIntl);
